import axios from "axios";
import Api from "@/services/Api";
import i18n from "../i18n";

import moment from "moment-mini";
import { getDateTimeFormat } from "../helpers/dateFormat";

function changeDatesToUTC(person) {
  // deep copy and change times
  let utcPerson = { ...person };
  utcPerson.statuses = person.statuses.map(status => {
    let utcStatus = { ...status };
    utcStatus.date = moment(status.date, getDateTimeFormat()).toISOString();
    return utcStatus;
  });
  utcPerson.places = person.places.map(place => {
    let utcPlace = { ...place };
    if (place.timeFrom && place.timeFrom !== "") {
      utcPlace.timeFrom = moment(place.timeFrom, getDateTimeFormat()).toISOString();
    }
    if (place.timeTo && place.timeTo !== "") {
      utcPlace.timeTo = moment(place.timeTo, getDateTimeFormat()).toISOString();
    }
    return utcPlace;
  });
  utcPerson.contacts = person.contacts.map(contact => {
    let utcContact = { ...contact };
    if (contact.timeFrom && contact.timeFrom !== "") {
      utcContact.timeFrom = moment(contact.timeFrom, getDateTimeFormat()).toISOString();
    }
    if (contact.timeTo && contact.timeTo !== "") {
      utcContact.timeTo = moment(contact.timeTo, getDateTimeFormat()).toISOString();
    }
    return utcContact;
  });
  utcPerson.homeQuarantines = person.homeQuarantines.map(homeQuarantine => {
    let utcHomeQuarantine = { ...homeQuarantine };
    if (homeQuarantine.timeFrom && homeQuarantine.timeFrom !== "") {
      utcHomeQuarantine.timeFrom = moment(homeQuarantine.timeFrom, getDateTimeFormat()).toISOString();
    }
    if (homeQuarantine.timeTo && homeQuarantine.timeTo !== "") {
      utcHomeQuarantine.timeTo = moment(homeQuarantine.timeTo, getDateTimeFormat()).toISOString();
    }
    return utcHomeQuarantine;
  });
  return utcPerson;
}

// store for cancels
const cancelSources = {};

/** cancels previous call and returns a new cancel token */

function ensureCancel(methodId) {
  // cancels previous
  if (cancelSources[methodId]) {
    cancelSources[methodId].cancel();
  }

  // generates and stores new token
  cancelSources[methodId] = axios.CancelToken.source();
  return cancelSources[methodId].token;
}

export default {
  getVersion() {
    return Api().get("version");
  },

  getConfig() {
    return Api().get("/api/config");
  },

  locationSearch(query) {
    return Api().get("/location/search", { params: { query } });
  },

  polygonSearch(params) {
    return Api().get("/api/lockdowns/search", { params });
  },

  getValues(field) {
    return Api().get("/api/people/values/" + field);
  },

  getPerson(id) {
    return Api().get("/api/people/person/" + id);
  },

  getPeople(query, pagination) {
    const filterBy = [];
    Object.keys(pagination.filterBy).map(key => {
      if (pagination.filterBy[key]) {
        filterBy.push(key + ":" + pagination.filterBy[key]);
      }
    });
    return Api().get("/api/people", {
      params: {
        lang: i18n.locale,
        query: query,
        pageSize: pagination.itemsPerPage,
        page: pagination.page,
        sortBy: pagination.sortBy,
        sortDesc: pagination.sortDesc,
        filterBy: filterBy
      }
    });
  },

  whoseContact(id) {
    return Api().get("/api/people/whose-contact/" + id);
  },

  whoseContacts(ids) {
    return Api().post("/api/people/whose-contacts", { ids })
  },

  savePerson(payload) {
    if (payload._id) {
      return Api().put(
        "/api/people/person/" + payload._id,
        changeDatesToUTC(payload)
      );
    } else {
      return Api().post("/api/people/person", changeDatesToUTC(payload));
    }
  },

  deletePerson(id) {
    return Api().delete("/api/people/person/" + id);
  },
  getPeopleStats(date) {
    return Api().get("/api/people/stats?date=" + date.toISOString());
  },

  getAreaStats(params) {
    return Api().get("/api/people/areastats", {
      params: params
    });
  },

  getAreaStatsXlsx(params) {
    return Api().get("/api/people/areastats/xlsx", {
      params: params,
      responseType: "blob"
    });
  },

  getAdminStat(_params) {
    let params = new URLSearchParams();
    params.append("date", _params.date.toISOString());
    if (_params.byPrimaryAddress) {
      params.append("byPrimaryAddress", _params.byPrimaryAddress);
    }
    return Api().get(
      "/api/people/adminstat/" +
      _params.point.lng +
      "/" +
      _params.point.lat +
      "?" +
      params.toString()
    );
  },

  searchPeopleByAzon(azon, params) {
    return Api().get("/api/people/search?azon=" + azon, { params });
  },

  getPeopleByIds(ids) {
    return Api().post("/api/people/queryByIds", { ids });
  },

  isPhoneExists(phone) {
    return Api().get("/api/people/phonecheck", { params: { phone } });
  },

  createLockdown(payload) {
    return Api().post("/api/lockdowns", payload);
  },

  updateLockdown(id, payload) {
    return Api().put("/api/lockdowns/" + id, payload);
  },

  removeLockdown(id) {
    return Api().delete("/api/lockdowns/" + id);
  },

  getLockdowns({ date }) {
    let params = new URLSearchParams();
    params.append("date", date.toISOString());
    return Api().get("/api/lockdowns?" + params.toString());
  },

  getHeatmap({ date, status, personId, byPrimaryAddress, withFade }) {
    let params = new URLSearchParams();
    params.append("date", date.toISOString());
    if (status) {
      params.append("status", status);
    }
    if (byPrimaryAddress) {
      params.append("byPrimaryAddress", byPrimaryAddress);
    }
    if (withFade) {
      params.append("fade", true)
    }

    const cancelToken = ensureCancel("getHeatmap");

    return Api()
      .get(
        "api/heatmap" +
        (personId ? "/" + personId : "") +
        "?" +
        params.toString(),
        { cancelToken }
      )
      .catch(error => {
        if (!axios.isCancel(error)) {
          throw error;
        }
      });
  },

  getAdminAreaMap({ date, status, byPrimaryAddress, adminTypes, geomType }) {
    // possibl evalue og adminType city, district, county
    let params = new URLSearchParams();
    params.append("date", date.toISOString());
    if (status) {
      params.append("status", status);
    }
    if (byPrimaryAddress) {
      params.append("byPrimaryAddress", byPrimaryAddress);
    }
    if (adminTypes) {
      let typesString = adminTypes.join(",");
      params.append("adminTypes", typesString);
    }
    if (geomType) {
      params.append("geomType", geomType);
    }

    const cancelToken = ensureCancel("getAdminAreaMap");

    return Api()
      .get("api/adminareamap?" + params.toString(), {
        cancelToken
      })
      .catch(error => {
        if (!axios.isCancel(error)) {
          throw error;
        }
      });
  },

  getAdminAreas({ adminType, geomType }) {
    let params = new URLSearchParams();
    if (geomType) {
      params.append("geomType", geomType)
    }
    let url = "api/adminareamap/cities.geojson?"
    if (adminType === 'county') {
      url = "api/adminareamap/counties.geojson?"
    } else if (adminType === 'district') {
      url = "api/adminareamap/districts.geojson?"
    }

    const cancelToken = ensureCancel("getAdminAreas");

    return Api()
      .get(url + params.toString(), {
        cancelToken
      })
      .catch(error => {
        if (!axios.isCancel(error)) {
          throw error;
        }
      });
  },

  getAdminAreaStatistics({ date, status, byPrimaryAddress, adminTypes, needHomeQuarantine, needInfected }) {
    let params = new URLSearchParams();
    params.append("date", date.toISOString());
    if (status) {
      params.append("status", status);
    }
    if (byPrimaryAddress) {
      params.append("byPrimaryAddress", byPrimaryAddress);
    }
    if (adminTypes) {
      let typesString = adminTypes.join(",");
      params.append("adminTypes", typesString);
    }
    if (needHomeQuarantine) {
      params.append("needHomeQuarantine", needHomeQuarantine);
    }
    if (needInfected) {
      params.append("needInfected", needInfected);
    }

    const cancelToken = ensureCancel("getAdminAreaStatistics");

    return Api()
      .get("api/adminareamap/stats?" + params.toString(), {
        cancelToken
      })
      .catch(error => {
        if (!axios.isCancel(error)) {
          throw error;
        }
      });
  },

  import(data, params) {
    return Api().post("api/import/validate", data, { params });
  },

  duplicate(data) {
    return Api().post("api/import/duplicate", data);
  },

  bulkInsert(data, params) {
    return Api().post("api/import/bulk", data, { params });
  },

  getLatestHomeQuarantineDate() {
    return Api().get("api/import/latesthq");
  },

  getContactPlaces(date) {
    const cancelToken = ensureCancel("getContactPlaces");
    return Api()
      .get("/api/contact/places?date=" + date.toISOString(), { cancelToken })
      .catch(error => {
        if (!axios.isCancel(error)) {
          throw error;
        }
      });
  },
  getContactsForPlace({ date, statuses, addresses, byPrimaryAddress }) {
    return Api().post("/api/contact", {
      date,
      statuses,
      addresses,
      byPrimaryAddress
    });
  },

  getChartCounties() {
    return Api().get("/api/people/chart/places", {
      params: { getCounties: true }
    });
  },
  getChartCities(county) {
    return Api().get("/api/people/chart/places", {
      params: { getCounties: false, county }
    });
  },
  getChartStats(params) {
    return Api().get("/api/people/chart/stats", { params });
  },
  getChartAgesStats(params) {
    return Api().get("/api/people/chart/ages", { params });
  },
  getAsuraHeatmap({ date }) {
    let params = new URLSearchParams();
    params.append("date", date.toISOString());
    const cancelToken = ensureCancel("getAsuraHeatmap");
    return Api().get("/api/asura/heatmap?" + params.toString(), {
      cancelToken
    }).catch(error => {
      if (!axios.isCancel(error)) {
        throw error;
      }
    });
  },

  getMobileDevices() {
    return Api().get("/api/mobile/devices");
  },

  getMobileTrackingData(device) {
    return Api().get("/api/mobile/tracking/" + device);
  },
};
